/**
 * termPricing.ts — per-term full vs actual pricing (discounted-term display).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the CTO's pricing model treats the 1-month option's `monthlyPence` as the
 * per-month REFERENCE price; a longer term is configured cheaper per month, and
 * the plan-selection screen shows the saving against that reference — a
 * struck-through "was" total beside the actual total, plus a "Save £X" badge.
 * All arithmetic stays on integer pence — never invent a rounding: the full
 * price is `referenceMonthly × months`, the actual total is `term.monthlyPence
 * × months`, and the saving is their (non-negative) difference.
 *
 * The 1-month plan IS the reference, so its fullPence always equals its
 * actualPence by construction (its `months > 1` guard never passes) — it never
 * produces a saving, so callers never need to special-case it to suppress a
 * strike-through. Likewise, when the plan list carries no 1-month option at
 * all, there is no reference: every term's fullPence collapses to its own
 * actualPence, so nothing strikes and no badge shows.
 */
import type { PricePlan } from '../api/types';

export interface TermPricing {
  months: number;
  /** The term's stored per-month price (echoed for convenience). */
  monthlyPence: number;
  /** referenceMonthly × months — the "was" price for a discounted term. */
  fullPence: number;
  /** term.monthlyPence × months — the actual amount charged over the term. */
  actualPence: number;
  /** max(0, fullPence − actualPence) — 0 when no discount is configured. */
  savePence: number;
}

/**
 * Derive full-vs-actual pricing for every plan in a quote, one entry per input
 * plan (same order). See the module doc for the reference/edge-case rules.
 */
export function termPricing(plans: PricePlan[]): TermPricing[] {
  const reference = plans.find((p) => p.months === 1);
  return plans.map((plan) => {
    const actualPence = plan.monthlyPence * plan.months;
    const fullPence = plan.months > 1 && reference ? reference.monthlyPence * plan.months : actualPence;
    const savePence = Math.max(0, fullPence - actualPence);
    return { months: plan.months, monthlyPence: plan.monthlyPence, fullPence, actualPence, savePence };
  });
}
